import React, { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../SearchableSelect';
import {
  IMPRESION_MOTOR,
  IMPRESION_TARJETA_MIN_HEIGHT,
  IMPRESION_VARIANTE,
  MOSTRAR_CAMPOS_IMPRESORA,
  ayudaVariantePlantilla,
  esMotorRdlc,
  variantesPlantillaParaTipo,
  type ImpresionMotor,
} from '../../constants/sucVentaImpresion';
import sucVentaService, {
  type SucVentaCatalogoImpresion,
  type SucVentaImpresionLetra,
} from '../../services/sucVentaService';

function nombreArchivoCorto(ruta: string | undefined | null): string {
  if (!ruta) return '';
  const sep = ruta.includes('/') ? '/' : '\\';
  return ruta.split(sep).pop() ?? ruta;
}

interface ImpresionLetraCardProps {
  titulo: string;
  impresion: SucVentaImpresionLetra;
  impresora: string;
  catalogo: SucVentaCatalogoImpresion;
  /** Para etiquetas/ayuda: en recibos (tipo 4) se invierten plantillas 2 y 3. */
  nroTipoFormulario?: string | null;
  idCompania?: string;
  nroSuc?: number;
  onImpresionChange: (patch: Partial<SucVentaImpresionLetra>) => void;
  onImpresoraChange: (value: string) => void;
}

const ImpresionLetraCard: React.FC<ImpresionLetraCardProps> = ({
  titulo,
  impresion,
  impresora,
  catalogo,
  nroTipoFormulario,
  idCompania,
  nroSuc,
  onImpresionChange,
  onImpresoraChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const motor = impresion.motor ?? IMPRESION_MOTOR.PLANTILLA;
  const esRdlc = esMotorRdlc(motor);
  const varianteActual = impresion.variantePlantilla ?? IMPRESION_VARIANTE.PLANTILLA_1;

  const motorOptions =
    catalogo.motores.length > 0
      ? catalogo.motores
      : [
          { valor: IMPRESION_MOTOR.PLANTILLA, etiqueta: 'Plantillas' },
          { valor: IMPRESION_MOTOR.RDLC, etiqueta: 'RDLC (.rdlc)' },
        ];

  const varianteOptions = variantesPlantillaParaTipo(nroTipoFormulario);
  const ayudaVariante = ayudaVariantePlantilla(varianteActual, nroTipoFormulario);

  const handleMotorChange = (nuevoMotor: ImpresionMotor) => {
    onImpresionChange({
      motor: esMotorRdlc(nuevoMotor) ? IMPRESION_MOTOR.RDLC : IMPRESION_MOTOR.PLANTILLA,
    });
  };

  const handleUploadRdlc = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (ext !== '.rdlc' && ext !== '.rdl') {
      enqueueSnackbar('Solo se permiten archivos .rdlc o .rdl', { variant: 'warning' });
      return;
    }

    try {
      setUploading(true);
      const result = await sucVentaService.uploadRdlc(file, {
        idCompania: idCompania?.trim() || undefined,
        nroSuc,
      });
      onImpresionChange({
        motor: IMPRESION_MOTOR.RDLC,
        rutaRdlc: result.rutaRdlc,
      });
      enqueueSnackbar('Archivo .rdlc subido correctamente', { variant: 'success' });
    } catch (error: any) {
      const data = error?.response?.data;
      const msg =
        typeof data === 'string'
          ? data
          : data?.error ?? data?.message ?? data?.Message ?? 'Error al subir el archivo .rdlc';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadRdlc = async () => {
    const ruta = (impresion.rutaRdlc || '').trim();
    if (!ruta) {
      enqueueSnackbar('No hay un archivo .rdlc configurado para descargar', { variant: 'warning' });
      return;
    }

    try {
      setDownloading(true);
      await sucVentaService.downloadRdlc(ruta);
      enqueueSnackbar('Archivo .rdlc descargado', { variant: 'success' });
    } catch (error: any) {
      const data = error?.response?.data;
      let msg = 'Error al descargar el archivo .rdlc';
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          msg = parsed?.error ?? parsed?.message ?? parsed?.Message ?? msg;
        } catch {
          /* mantener mensaje genérico */
        }
      } else if (typeof data === 'string') {
        msg = data;
      } else if (data?.error ?? data?.message ?? data?.Message) {
        msg = data.error ?? data.message ?? data.Message;
      }
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Grid item xs={12} md={6}>
      <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', minHeight: IMPRESION_TARJETA_MIN_HEIGHT }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, height: '100%', boxSizing: 'border-box' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {titulo}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={esRdlc ? IMPRESION_MOTOR.RDLC : motor}
                  onChange={(e) => handleMotorChange(e.target.value as ImpresionMotor)}
                >
                  {motorOptions.map((opcion) => (
                    <FormControlLabel
                      key={opcion.valor}
                      value={opcion.valor}
                      control={<Radio size="small" />}
                      label={opcion.etiqueta}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={MOSTRAR_CAMPOS_IMPRESORA ? 8 : 12} sx={{ display: esRdlc ? 'block' : 'none' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                <Tooltip title={(impresion.rutaRdlc || '').trim() || 'Sin archivo seleccionado'}>
                  <TextField
                    label="Archivo .rdlc"
                    value={nombreArchivoCorto(impresion.rutaRdlc)}
                    fullWidth
                    placeholder="Sin archivo seleccionado"
                    InputProps={{ readOnly: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Tooltip>
                <Tooltip title="Descargar .rdlc para editar">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={handleDownloadRdlc}
                      disabled={downloading || uploading || !(impresion.rutaRdlc || '').trim()}
                      aria-label="Descargar archivo .rdlc"
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        minHeight: 56,
                        minWidth: 56,
                      }}
                    >
                      {downloading ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={uploading || downloading}
                  aria-label="Seleccionar archivo .rdlc"
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                    minHeight: 56,
                    minWidth: 56,
                    px: 1.5,
                    fontSize: '1.25rem',
                    lineHeight: 1,
                  }}
                >
                  {uploading ? <CircularProgress size={18} /> : '…'}
                  <input
                    type="file"
                    accept=".rdlc,.rdl,application/xml,text/xml"
                    hidden
                    onChange={handleUploadRdlc}
                  />
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={esRdlc ? 12 : MOSTRAR_CAMPOS_IMPRESORA ? 6 : 12} sx={{ display: esRdlc ? 'none' : 'block' }}>
              <SearchableSelect
                label="Variante de plantilla"
                value={varianteActual}
                onChange={(val) =>
                  onImpresionChange({
                    variantePlantilla: val ?? IMPRESION_VARIANTE.PLANTILLA_1,
                  })
                }
                options={varianteOptions.map((v) => ({ value: v.valor, label: v.etiqueta }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              {ayudaVariante ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {ayudaVariante}
                </Typography>
              ) : null}
            </Grid>

            {MOSTRAR_CAMPOS_IMPRESORA ? (
              <Grid item xs={12} md={esRdlc ? 4 : 6}>
                <TextField
                  label="Impresora"
                  value={impresora}
                  onChange={(e) => onImpresoraChange(e.target.value)}
                  fullWidth
                  helperText="Nombre exacto como aparece en Windows."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            ) : null}
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ImpresionLetraCard;
